'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Trophy } from 'lucide-react';
import { apiFetch } from '@/lib/api';

type Placement = {
  axis: string;
  scope: string;
  period: string;
  periodStart: string;
  rank: number;
  score: number;
};

// Must track the five axes the board actually serves. This list had drifted:
// 'galleries' and 'videos' were missing (added 2026-07-12) and 'comments-received'
// was still here after the axis was hidden, so a member whose best placement was
// on follows or videos got the raw slug rendered ("the follows-gained board").
// Labels name the METRIC, matching the pills on /leaderboards.
const AXIS_LABEL: Record<string, string> = {
  views: 'views',
  posts: 'post likes',
  galleries: 'gallery likes',
  videos: 'video views',
  'follows-gained': 'follows gained',
};

/**
 * Single-row teaser for the user's best all-time global placement. Suppress
 * entirely when there's no top-50 placement — no "you're not on any boards
 * yet" engagement-bait copy per design system rules. Weekly/scoped boards
 * are not surfaced since the 2026-07-08 leaderboard simplification.
 */
export default function LeaderboardRail() {
  const [best, setBest] = useState<Placement | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/leaderboards/me', undefined, { method: 'GET' });
        if (!res.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const json = (await res.json()) as { placements: Placement[] };
        const candidates = (json.placements ?? []).filter(
          (p) => p.period === 'all-time' && p.scope === 'global',
        );
        candidates.sort((a, b) => a.rank - b.rank);
        if (!cancelled) {
          setBest(candidates[0] ?? null);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || !best) return null;

  const axis = AXIS_LABEL[best.axis] ?? best.axis;

  return (
    <Link
      href={`/leaderboards?axis=${encodeURIComponent(best.axis)}`}
      className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/40 hover:shadow-sm transition-all"
    >
      <span className="ring-chip inline-grid h-9 w-9 place-items-center rounded-lg shrink-0"><Trophy className="h-4 w-4" strokeWidth={1.75} /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          You&apos;re <span className="font-semibold tabular-nums">#{best.rank}</span> on the {axis} board
        </div>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">See all leaderboards →</span>
    </Link>
  );
}
